import React from "react";

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import clsx from "clsx";
import { observer } from "mobx-react-lite";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  useStyles,
} from "../utils/Icons";

type DateArguments = {
  label: string;
  dateFormat: string;
};

interface Props {
  element: FormElement<DateArguments>;
}

const FormDate: React.FC<Props> = observer(({ element }) => {
  const editing = useEditing(element);
  const { classes } = useStyles();

  return (
    <>
      {!editing.value ? (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon
            className={clsx(["styled-drag-indicator"])}
          />
          <div className={classes.longTextInputContent}>
            <Typography
              variant="h6"
              color="textPrimary"
              style={{ marginBottom: 12 }}
            >
              {element.arguments.label || "Date label"}
            </Typography>
            <DatePicker disableFuture />
          </div>

          <div className={clsx([classes.editIcons, "edit-icons-container"])}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon onClick={() => element.remove()} />
          </div>
        </Box>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.columnContent}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Date label
              </Typography>
              <TextField
                placeholder="Enter date label"
                value={element.arguments.label}
                onChange={(e) => element.setArgument("label", e.target.value)}
              />

              <Typography
                variant="body2"
                color="textSecondary"
                style={{
                  alignSelf: "flex-start",
                  marginBottom: 8,
                  marginTop: 8,
                }}
              >
                Select date format
              </Typography>

              <Box display="flex" flexDirection="row">
                <Button
                  variant="outlined"
                  color="primary"
                  className={clsx(classes.dateFormatButton, {
                    [classes.selectedDateFormatButton]:
                      element.arguments.dateFormat == "mm/dd/yyyy",
                  })}
                  onClick={() =>
                    element.setArgument("dateFormat", "mm/dd/yyyy")
                  }
                >
                  mm/dd/yyyy
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  className={clsx(classes.dateFormatButton, {
                    [classes.selectedDateFormatButton]:
                      element.arguments.dateFormat == "mm/dd/yy",
                  })}
                  onClick={() => element.setArgument("dateFormat", "mm/dd/yy")}
                >
                  mm/dd/yy
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: 4 }}
                  className={clsx(classes.dateFormatButton, {
                    [classes.selectedDateFormatButton]:
                      element.arguments.dateFormat == "mmm,dd,yyyy",
                  })}
                  onClick={() =>
                    element.setArgument("dateFormat", "mmm,dd,yyyy")
                  }
                >
                  Mon,dd,yyyy
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  className={clsx(classes.dateFormatButton, {
                    [classes.selectedDateFormatButton]:
                      element.arguments.dateFormat == "mmmm,dd,yyyy",
                  })}
                  onClick={() =>
                    element.setArgument("dateFormat", "mmmm,dd,yyyy")
                  }
                >
                  Month,dd,yyyy
                </Button>
              </Box>

              <StyledHorizontalDivider orientation="horizontal" />
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        aria-label="Required"
                        checked={element.required}
                        onChange={(e) => element.setRequired(e.target.checked)}
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label="Required"
                  />
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button
                    className={classes.saveChangesButton}
                    onClick={editing.stop}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Box>
          </Box>
          <div className={classes.editIcons}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon />
          </div>
        </Box>
      )}
    </>
  );
});

export default FormDate;
